import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "react-responsive-modal";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import { toast } from "react-toastify";
import EditSalaryMappingComponent from "./EditSalaryMappingComponent";

import {
  useManageSalaryPolicyMapping,
  useViewSalaryComponents,
  useViewSalaryTemplateDetails,
} from "../../queries/payrollQuries";

const AddOrEditSalaryMapping = ({ openModal, onCloseModal, policyData }) => {
  const { handleSubmit } = useForm();

  const { data: viewSalaryComponents, isLoading: isLoadingComponents } =
    useViewSalaryComponents();

  const { data: viewSalaryTemplateDetails, isLoading: isLoadingTemplate } =
    useViewSalaryTemplateDetails({ policy_id: policyData?.id });

  const { mutate: manageSalaryPolicyMapping, isLoading: isSavingPolicy } =
    useManageSalaryPolicyMapping();

  const [combinedSalaryComponents, setCombinedSalaryComponents] = useState([]);
  const [toggleStates, setToggleStates] = useState({});
  const [draftChanges, setDraftChanges] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);

  useEffect(() => {
    if (
      !Array.isArray(viewSalaryComponents?.data) ||
      isLoadingComponents ||
      isLoadingTemplate
    )
      return;

    const templateMap =
      viewSalaryTemplateDetails?.data?.reduce((acc, comp) => {
        acc[comp.salary_component_id] = comp;
        return acc;
      }, {}) || {};

    const merged = viewSalaryComponents.data.map((comp) => {
      const override = templateMap[comp.salary_component_id];

      return {
        ...comp,
        template_is_active: override ? override.is_active : false,
        to_be_calculated_value:
          override?.to_be_calculated_value ??
          comp.to_be_calculated_value ??
          0,
        calculation_type:
          override?.calculation_type ?? comp.calculation_type ?? 0,
        calculated_from:
          override?.calculated_from ?? comp.calculated_from ?? "",
        calculated_from_value:
          override?.calculated_from_value ??
          comp.calculated_from_value ??
          0,
        is_fixed: override?.is_fixed ?? comp.is_fixed ?? false,
      };
    });

    const initial = merged.reduce((acc, c, i) => {
      acc[i] = c.template_is_active;
      return acc;
    }, {});

    setCombinedSalaryComponents(merged);
    setToggleStates(initial);
  }, [
    viewSalaryComponents,
    viewSalaryTemplateDetails,
    isLoadingComponents,
    isLoadingTemplate,
  ]);

  const handleToggle = (index) => {
    const comp = combinedSalaryComponents[index];
    if (!comp.is_active) return;

    const newState = !toggleStates[index];

    setToggleStates((prev) => ({
      ...prev,
      [index]: newState,
    }));

    setDraftChanges((prev) => ({
      ...prev,
      [comp.salary_component_id]: {
        ...prev?.[comp.salary_component_id],
        ...comp,
        template_is_active: newState,
      },
    }));
  };

  const handleEdit = (component) => {
    if (!component.is_active) return;
    setSelectedComponent(component);
    setEditModalOpen(true);
  };

  const handleDraftChange = (updated) => {
    setDraftChanges((prev) => ({
      ...prev,
      [updated.salary_component_id]: updated,
    }));
  };

  const onSubmit = () => {
    const hasActive = Object.values(toggleStates).some((s) => s === true);

    if (!hasActive) {
      toast.info("Please activate at least one salary component.");
      return;
    }

    const mergedPayload = combinedSalaryComponents.map((comp) => {
      const override = draftChanges[comp.salary_component_id];
      const final = override ? { ...comp, ...override } : comp;

      return {
        component_id: Number(final.salary_component_id),
        component_name: final.earning_type,
        to_be_calculated_value: Number(final.to_be_calculated_value),
        calculation_type: Number(final.calculation_type),
        calculated_from: final.calculated_from || "",
        calculated_from_value: Number(final.calculated_from_value),
        component_type: Number(final.component_type),
        is_active: final.template_is_active,
        is_fixed: final.is_fixed,
      };
    });

    manageSalaryPolicyMapping(
      {
        policy_id: policyData.id,
        salary_component_details: mergedPayload,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message || "Salary policy updated!");
          onCloseModal();
        },
        onError: (err) => {
          toast.error(
            err?.response?.data?.message ||
              "Failed to update salary policy. Try again."
          );
        },
      }
    );
  };

  const loading = isLoadingComponents || isLoadingTemplate;

  return (
    <>
      <Modal open={openModal} onClose={onCloseModal} center>
        <div className="p-5 w-[520px]">
          <h2 className="text-lg font-semibold mb-5">Customize Payroll Policy</h2>

          {loading ? (
            <div className="text-gray-500 text-center py-5">
              Loading salary components...
            </div>
          ) : combinedSalaryComponents.length === 0 ? (
            <div className="text-red-500 text-center py-5">
              No salary components found.
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <table className="w-full text-sm">
                <thead className="text-xs bg-gray-50 font-semibold text-gray-600">
                  <tr>
                    <th className="px-4 py-3">Component</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Enable</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>

                <tbody>
                  {combinedSalaryComponents.map((c, index) => {
                    const disabled = !c.is_active;

                    return (
                      <tr
                        key={c.salary_component_id}
                        className={`border-b ${
                          disabled ? "bg-gray-50 text-gray-400" : ""
                        }`}
                      >
                        <td className="px-4 py-3">{c.earning_type}</td>
                        <td className="px-4 py-3">{c.type_name}</td>

                        <td className="px-4 py-3">
                          <ToggleSwitch
                            checked={toggleStates[index]}
                            disabled={disabled}
                            getToggleState={() => handleToggle(index)}
                          />
                        </td>

                        <td className="px-4 py-3">
                          <img
                            src="/images/edit-black-icon.svg"
                            alt="Edit"
                            className={`cursor-pointer ${
                              disabled ? "opacity-40 pointer-events-none" : ""
                            }`}
                            onClick={() => handleEdit(c)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  className="btn btn--border"
                  onClick={onCloseModal}
                >
                  Cancel
                </button>

                <button type="submit" className="btn" disabled={isSavingPolicy}>
                  {isSavingPolicy ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      {editModalOpen && selectedComponent && (
        <EditSalaryMappingComponent
          openModal={editModalOpen}
          onCloseModal={() => setEditModalOpen(false)}
          salaryComponentData={selectedComponent}
          policyData={policyData}
          onDraftChange={handleDraftChange}
        />
      )}
    </>
  );
};

export default AddOrEditSalaryMapping;
